import { useState } from "react";
import axios from "axios";
import { Plus, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}`;

const emptyResource = () => ({ skill: "", years_experience: "", required_by: "" });

export default function LookingFor() {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    email: "",
    company: "",
  });
  const [resources, setResources] = useState([emptyResource()]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateResource = (idx, k, v) =>
    setResources((rs) => rs.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));
  const addResource = () => setResources((rs) => [...rs, emptyResource()]);
  const removeResource = (idx) =>
    setResources((rs) => (rs.length > 1 ? rs.filter((_, i) => i !== idx) : rs));

  const validate = () => {
    if (!form.name.trim() || !form.contact.trim() || !form.email.trim()) {
      toast.error("Please fill in your name, contact, and email.");
      return false;
    }
    for (let i = 0; i < resources.length; i++) {
      const r = resources[i];
      if (!r.skill.trim() || r.years_experience === "" || !r.required_by) {
        toast.error(`Please complete resource #${i + 1}.`);
        return false;
      }
      const yrs = Number(r.years_experience);
      if (Number.isNaN(yrs) || yrs < 0 || yrs > 60) {
        toast.error(`Years of experience for resource #${i + 1} must be 0–60.`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        contact: form.contact.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        resources: resources.map((r) => ({
          skill: r.skill.trim(),
          years_experience: Number(r.years_experience),
          required_by: r.required_by,
        })),
      };
      await axios.post(`${API}/looking-for`, payload);
      setSubmitted(true);
      toast.success("Request received. We'll be in touch shortly.");
      setForm({ name: "", contact: "", email: "", company: "" });
      setResources([emptyResource()]);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 502 || status === 504 || !status) {
        msg =
          "We received your details but couldn't send the notification email right now. Please try again shortly, or email us directly at contact@t3partner.in.";
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = "Something went wrong. Please try again.";
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="looking-for-page">
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
        <div className="text-xs uppercase tracking-[0.18em] text-orange-600 font-semibold mb-4 mm-fade-up">
          What you're looking for
        </div>
        <h1 className="mm-fade-up mm-delay-1 font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900 max-w-3xl leading-[1.05]">
          Tell us what you need.
        </h1>
        <p className="mm-fade-up mm-delay-2 mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
          Share your requirements and we'll shortlist the right talent — typically within 48 hours.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        {submitted ? (
          <div
            data-testid="looking-for-success"
            className="border border-slate-200 rounded-xl p-10 bg-white text-center"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-5">
              <CheckCircle2 size={28} />
            </div>
            <h2 className="font-heading text-2xl font-medium text-slate-900">
              Request received.
            </h2>
            <p className="text-slate-600 mt-3 max-w-md mx-auto leading-relaxed">
              Thank you. We've logged your requirements and a notification has been queued to{" "}
              <span className="font-medium text-slate-900">contact@t3partner.in</span>.
              Our team will reach out shortly.
            </p>
            <Button
              data-testid="looking-for-submit-another"
              onClick={() => setSubmitted(false)}
              className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-md h-11 px-6"
            >
              Submit another request
            </Button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            data-testid="looking-for-form"
            className="border border-slate-200 rounded-xl p-6 sm:p-10 bg-white space-y-10"
          >
            {/* Contact details */}
            <div>
              <h3 className="font-heading text-lg font-medium text-slate-900 mb-1">
                Your details
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                We'll use these to get back to you.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="wylf-name" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                    Name
                  </Label>
                  <Input
                    id="wylf-name"
                    data-testid="wylf-name-input"
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    required
                    className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <Label htmlFor="wylf-contact" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                    Contact number
                  </Label>
                  <Input
                    id="wylf-contact"
                    data-testid="wylf-contact-input"
                    value={form.contact}
                    onChange={(e) => updateField("contact", e.target.value)}
                    required
                    className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                    placeholder="+1 555 000 1234"
                  />
                </div>
                <div>
                  <Label htmlFor="wylf-email" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                    Email
                  </Label>
                  <Input
                    id="wylf-email"
                    data-testid="wylf-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    required
                    className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="wylf-company" className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                    Company <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Input
                    id="wylf-company"
                    data-testid="wylf-company-input"
                    value={form.company}
                    onChange={(e) => updateField("company", e.target.value)}
                    className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0"
                    placeholder="Acme Inc."
                  />
                </div>
              </div>
            </div>

            {/* Resources required */}
            <div>
              <div className="flex items-end justify-between gap-4 mb-6">
                <div>
                  <h3 className="font-heading text-lg font-medium text-slate-900 mb-1">
                    Resources required
                  </h3>
                  <p className="text-sm text-slate-500">
                    Add one card per role. You can add as many as you need.
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                {resources.map((r, idx) => (
                  <div
                    key={idx}
                    data-testid={`wylf-resource-card-${idx}`}
                    className="border border-slate-200 rounded-lg p-5 sm:p-6 bg-slate-50/60"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
                        Resource #{idx + 1}
                      </span>
                      {resources.length > 1 && (
                        <button
                          type="button"
                          data-testid={`wylf-remove-resource-${idx}`}
                          onClick={() => removeResource(idx)}
                          className="text-slate-500 hover:text-red-600 text-xs inline-flex items-center gap-1.5 transition-colors"
                        >
                          <Trash2 size={14} /> Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div className="md:col-span-1">
                        <Label htmlFor={`skill-${idx}`} className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                          Skill requirement
                        </Label>
                        <Input
                          id={`skill-${idx}`}
                          data-testid={`wylf-resource-skill-${idx}`}
                          value={r.skill}
                          onChange={(e) => updateResource(idx, "skill", e.target.value)}
                          required
                          className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0 bg-white"
                          placeholder="e.g., Senior React Engineer"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`years-${idx}`} className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                          Years of experience
                        </Label>
                        <Input
                          id={`years-${idx}`}
                          data-testid={`wylf-resource-years-${idx}`}
                          type="number"
                          min={0}
                          max={60}
                          value={r.years_experience}
                          onChange={(e) => updateResource(idx, "years_experience", e.target.value)}
                          required
                          className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0 bg-white"
                          placeholder="e.g., 5"
                        />
                      </div>
                      <div>
                        <Label htmlFor={`date-${idx}`} className="text-xs uppercase tracking-wider text-slate-600 font-medium">
                          Required by
                        </Label>
                        <Input
                          id={`date-${idx}`}
                          data-testid={`wylf-resource-date-${idx}`}
                          type="date"
                          value={r.required_by}
                          onChange={(e) => updateResource(idx, "required_by", e.target.value)}
                          required
                          className="mt-2 h-11 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                data-testid="wylf-add-resource-btn"
                onClick={addResource}
                variant="outline"
                className="mt-5 border-dashed border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md h-11"
              >
                <Plus className="mr-2" size={16} /> Add another resource
              </Button>
            </div>

            {/* Submit */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500 max-w-md">
                By submitting, you agree we may contact you regarding your request. Submissions are delivered to{" "}
                <span className="text-slate-900 font-medium">contact@t3partner.in</span>.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="wylf-submit-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-12 px-8 text-sm font-semibold tracking-wide w-full sm:w-auto"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={16} /> Submitting…
                  </>
                ) : (
                  "Submit request"
                )}
              </Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
